import { Flex, Grid, Group, Select, Text, TextInput, Textarea } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { showFullName } from 'utils/helpers';
import { useForm } from '@mantine/form';
import { PortalButton } from 'components/Button/PortalButton';
import { useDrawerContext } from 'context/hooks';
import { BsBell } from 'react-icons/bs';
import { SendNotificationValues } from 'models/notification';
import { useCustomMutation } from 'hooks/api/useCustomMutation';
import { sendNotification } from 'services/notification';
import { validateRequired } from 'utils/validation';
import { usePatient } from 'hooks/api/useUser';
import { PatientIdProps } from 'models/general';
import { FormFieldDrawer } from './FormFieldDrawer';

export const SendNotificationForm = ({ patientId }: PatientIdProps) => {
  const { t } = useTranslation();
  const { closeDrawer } = useDrawerContext();
  const { data: patientData } = usePatient({ patientId });
  const activeGroups = patientData?.visibleEnlistments || [];
  const form = useForm<SendNotificationValues>({
    validateInputOnChange: true,
    initialValues: {
      userId: patientData?.id,
      groupId: patientData?.visibleEnlistments[0].groupId,
      title: '',
      body: '',
    },
    validate: {
      groupId: (value) => validateRequired({ value, label: t('group_label') }),
      body: (value) => validateRequired({ value, label: t('body_label') }),
    },
  });

  const { mutate: sendingNotification, isPending } = useCustomMutation({
    fn: sendNotification,
    onSuccess: { message: t('send_notification_success'), callback: () => closeDrawer() },
  });

  return (
    <form onSubmit={form.onSubmit((values) => sendingNotification(values))}>
      <FormFieldDrawer label={t('notification_to_label')}>
        <Text>{showFullName(patientData)}</Text>
      </FormFieldDrawer>
      <FormFieldDrawer label={t('group_label')}>
        <Select
          data={activeGroups.map((group) => ({
            label: group.name,
            value: group.groupId,
          }))}
          placeholder={t('group_placeholder')}
          disabled={isPending}
          {...form.getInputProps('groupId')}
        />
      </FormFieldDrawer>
      <FormFieldDrawer label={t('title_label')}>
        <TextInput {...form.getInputProps('title')} disabled={isPending} maxLength={100} />
      </FormFieldDrawer>
      <FormFieldDrawer label={t('body_label')}>
        <Textarea
          required
          {...form.getInputProps('body')}
          disabled={isPending}
          autosize
          minRows={5}
          maxRows={5}
          maxLength={500}
        />
      </FormFieldDrawer>
      <Grid my={'md'}>
        <Grid.Col span={11}>
          <Flex justify={'flex-start'}>
            <Group>
              <PortalButton
                Icon={BsBell}
                label={'Send'}
                type={'submit'}
                disabled={!form.isValid()}
                loading={isPending}
              />
              <PortalButton
                label={t('cancel_label')}
                onClick={() => closeDrawer()}
                variant={'outline'}
                loading={isPending}
              />
            </Group>
          </Flex>
        </Grid.Col>
      </Grid>
    </form>
  );
};
